import { useState } from 'react';
import {
  Plus,
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  Trash2,
  Pencil,
  Building2,
  User,
  Kanban,
  List,
  MoreHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { useTasks } from '@/lib/hooks/crm/useTasks';
import { useContacts } from '@/lib/hooks/crm/useContacts';
import { useCompanies } from '@/lib/hooks/crm/useCompanies';
import type { CrmTask, CrmCreateTaskInput, CrmTaskPriority, CrmTaskStatus } from '@/lib/api';

const priorityConfig: Record<CrmTaskPriority, { label: string; color: string }> = {
  high: { label: 'High', color: 'bg-red-100 text-red-700' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  low: { label: 'Low', color: 'bg-blue-100 text-blue-700' },
};

const statusConfig: Record<CrmTaskStatus, { label: string; icon: React.ElementType }> = {
  pending: { label: 'Pending', icon: Circle },
  in_progress: { label: 'In Progress', icon: Clock },
  completed: { label: 'Completed', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', icon: AlertTriangle },
};

const emptyForm: CrmCreateTaskInput = {
  title: '',
  description: '',
  priority: 'medium',
  dueDate: '',
};

export function TasksView() {
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const [filterStatus, setFilterStatus] = useState<string>('active');
  const [filterPriority, setFilterPriority] = useState<string>('all');

  const taskOptions: Record<string, string | boolean | undefined> = {};
  if (filterStatus === 'active') {
    // Show pending + in_progress
  } else if (filterStatus !== 'all') {
    taskOptions.status = filterStatus;
  }
  if (filterPriority !== 'all') {
    taskOptions.priority = filterPriority;
  }

  const {
    tasks: allTasks,
    loading,
    refetch,
    updateTask: updateTaskFn,
    completeTask,
    deleteTask,
  } = useTasks(
    filterStatus === 'active'
      ? { ...taskOptions, sortBy: 'dueDate' }
      : { ...taskOptions, sortBy: 'dueDate' },
  );
  const { contacts } = useContacts({ limit: 200 });
  const { companies } = useCompanies({ limit: 200 });

  // Client-side filter for "active" (pending + in_progress)
  const tasks =
    filterStatus === 'active'
      ? allTasks.filter((t) => t.status === 'pending' || t.status === 'in_progress')
      : allTasks;

  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<CrmTask | null>(null);
  const [form, setForm] = useState<CrmCreateTaskInput>({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);

  function openCreate() {
    setEditingTask(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  }

  function openEdit(task: CrmTask) {
    setEditingTask(task);
    setForm({
      title: task.title,
      description: task.description ?? '',
      contactId: task.contactId ?? undefined,
      companyId: task.companyId ?? undefined,
      dealId: task.dealId ?? undefined,
      priority: task.priority,
      dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
    });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.title.trim()) {
      toast({ title: 'Validation', description: 'Title is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const api = (await import('@/lib/api')).default;
      if (editingTask) {
        await api.updateCrmTask(editingTask.id, form);
        toast({ title: 'Task updated' });
      } else {
        await api.createCrmTask(form);
        toast({ title: 'Task created' });
      }
      setShowForm(false);
      refetch();
    } catch {
      toast({ title: 'Error', description: 'Failed to save task', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  async function handleComplete(task: CrmTask) {
    try {
      await completeTask(task.id);
      toast({ title: 'Task completed' });
    } catch {
      toast({ title: 'Error', description: 'Failed to complete task', variant: 'destructive' });
    }
  }

  async function handleDelete(task: CrmTask) {
    try {
      await deleteTask(task.id);
      toast({ title: 'Task deleted' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete task', variant: 'destructive' });
    }
  }

  function isOverdue(task: CrmTask) {
    if (!task.dueDate || task.status === 'completed' || task.status === 'cancelled') return false;
    return new Date(task.dueDate) < new Date();
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="icon"
              className="h-8 w-8 rounded-r-none"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'board' ? 'secondary' : 'ghost'}
              size="icon"
              className="h-8 w-8 rounded-l-none"
              onClick={() => setViewMode('board')}
            >
              <Kanban className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
        </div>
      </div>

      {/* Task List / Board */}
      {viewMode === 'list' ? (
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">No tasks</p>
              <p className="text-sm mt-1">Create tasks to track your to-dos</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tasks.map((task) => {
                const prioConfig = priorityConfig[task.priority];
                const statusCfg = statusConfig[task.status];
                const StatusIcon = statusCfg.icon;
                const overdue = isOverdue(task);

                return (
                  <div
                    key={task.id}
                    className={`flex items-center gap-3 border rounded-lg p-3 hover:shadow-sm transition-shadow ${
                      task.status === 'completed' ? 'opacity-60' : ''
                    } ${overdue ? 'border-red-300 bg-red-50/50' : 'bg-card'}`}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0"
                      onClick={() => handleComplete(task)}
                      disabled={task.status === 'completed' || task.status === 'cancelled'}
                    >
                      <StatusIcon
                        className={`h-5 w-5 ${
                          task.status === 'completed' ? 'text-green-500' : 'text-muted-foreground'
                        }`}
                      />
                    </Button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p
                          className={`font-medium text-sm truncate ${
                            task.status === 'completed' ? 'line-through' : ''
                          }`}
                        >
                          {task.title}
                        </p>
                        <Badge
                          variant="secondary"
                          className={`text-xs flex-shrink-0 ${prioConfig.color}`}
                        >
                          {prioConfig.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {task.dueDate && (
                          <span
                            className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}
                          >
                            <Clock className="h-3 w-3" />
                            {overdue && 'Overdue: '}
                            {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        )}
                        {task.contactName && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {task.contactName}
                          </span>
                        )}
                        {task.companyName && (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {task.companyName}
                          </span>
                        )}
                        {task.dealName && (
                          <span className="flex items-center gap-1">
                            <Kanban className="h-3 w-3" />
                            {task.dealName}
                          </span>
                        )}
                      </div>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(task)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDelete(task)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* Board View */
        <div className="flex gap-4 p-6 overflow-x-auto flex-1 items-start">
          {loading
            ? [...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-full w-64 min-w-64 flex-shrink-0" />
              ))
            : (['pending', 'in_progress', 'completed', 'cancelled'] as const).map((status) => {
                const cfg = statusConfig[status];
                const StatusIcon = cfg.icon;
                const columnTasks = allTasks.filter((t) => t.status === status);

                return (
                  <div
                    key={status}
                    className="w-64 min-w-64 flex-shrink-0 flex flex-col rounded-lg border bg-muted/30"
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      const taskId = e.dataTransfer.getData('text/plain');
                      setDraggedTaskId(null);
                      if (!taskId) return;
                      try {
                        if (status === 'completed') {
                          await completeTask(taskId);
                        } else {
                          await updateTaskFn(taskId, { status } as never);
                        }
                        refetch();
                      } catch {
                        toast({
                          title: 'Error',
                          description: 'Failed to update task',
                          variant: 'destructive',
                        });
                      }
                    }}
                  >
                    <div className="p-3 border-b">
                      <div className="flex items-center gap-2">
                        <StatusIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">{cfg.label}</span>
                        <Badge variant="secondary" className="text-xs">
                          {columnTasks.length}
                        </Badge>
                      </div>
                    </div>

                    <div className="p-2 flex flex-col gap-2 min-h-[100px]">
                      {columnTasks.map((task) => {
                        const prioConfig = priorityConfig[task.priority];
                        const overdue = isOverdue(task);

                        return (
                          <div
                            key={task.id}
                            draggable
                            onDragStart={(e) => {
                              setDraggedTaskId(task.id);
                              e.dataTransfer.effectAllowed = 'move';
                              e.dataTransfer.setData('text/plain', task.id);
                            }}
                            onDragEnd={() => setDraggedTaskId(null)}
                            className={`border rounded-lg p-2.5 bg-card cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${
                              draggedTaskId === task.id ? 'opacity-50' : ''
                            } ${overdue ? 'border-red-300' : ''}`}
                          >
                            <div className="flex items-start justify-between gap-1">
                              <p className="font-medium text-sm truncate flex-1">{task.title}</p>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 -mr-1 -mt-0.5"
                                  >
                                    <MoreHorizontal className="h-3.5 w-3.5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEdit(task)}>
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleDelete(task)}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                              <Badge variant="secondary" className={`text-xs ${prioConfig.color}`}>
                                {prioConfig.label}
                              </Badge>
                              {task.dueDate && (
                                <span
                                  className={`text-xs ${overdue ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}
                                >
                                  {new Date(task.dueDate).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            {(task.contactName || task.companyName) && (
                              <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                                {task.contactName && (
                                  <span className="flex items-center gap-1 truncate">
                                    <User className="h-3 w-3" />
                                    {task.contactName}
                                  </span>
                                )}
                                {task.companyName && (
                                  <span className="flex items-center gap-1 truncate">
                                    <Building2 className="h-3 w-3" />
                                    {task.companyName}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Task' : 'Add Task'}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Follow up with prospect"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Priority</Label>
                <Select
                  value={form.priority ?? 'medium'}
                  onValueChange={(v) => setForm({ ...form, priority: v as CrmTaskPriority })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={form.dueDate ?? ''}
                  onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Contact</Label>
                <Select
                  value={form.contactId ?? ''}
                  onValueChange={(v) => setForm({ ...form, contactId: v || undefined })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Company</Label>
                <Select
                  value={form.companyId ?? ''}
                  onValueChange={(v) => setForm({ ...form, companyId: v || undefined })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="Task details..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingTask ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
